/**
 * NodeInfoBadges Component
 *
 * Displays compact pill badges below nodes showing key information like
 * model name, tool count, and connection status. Part of the "middle-ground"
 * node design system between minimal icons and full expanded views.
 *
 * Design principles:
 * - Minimal footprint: Badges add ~16-20px height when visible
 * - Zoom-responsive: Only shown at appropriate detail levels
 * - Accessible: Keyboard navigable with proper ARIA labels
 * - Scannable: Consistent visual language across node types
 */

import { memo, type ReactNode } from 'react';
import { makeStyles, tokens, mergeClasses, Tooltip } from '@coreai/fluentui-react';

export interface BadgeItem {
  /** Unique identifier for the badge */
  id: string;
  /** Icon to display (small, 12px) */
  icon?: ReactNode;
  /** Text label (kept short, max ~10 chars recommended) */
  label: string;
  /** Full text for tooltip */
  tooltip?: string;
  /** Visual variant */
  variant?: 'default' | 'active' | 'warning' | 'error';
  /** Click handler (makes badge interactive) */
  onClick?: () => void;
}

export interface NodeInfoBadgesProps {
  /** Array of badges to display */
  badges: BadgeItem[];
  /** Whether badges are visible (controlled by zoom level) */
  visible?: boolean;
  /** Maximum number of badges to show (excess shows "+N") */
  maxBadges?: number;
  /** Gap between badges */
  gap?: 'xs' | 'sm' | 'md';
  /** Additional class name */
  className?: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
    transitionProperty: 'opacity, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  containerHidden: {
    opacity: 0,
    transform: 'translateY(-4px)',
    pointerEvents: 'none',
    height: 0,
    marginTop: 0,
    overflow: 'hidden',
  },
  containerVisible: {
    opacity: 1,
    transform: 'translateY(0)',
  },
  gapXs: {
    gap: tokens.spacingHorizontalXXS,
  },
  gapSm: {
    gap: tokens.spacingHorizontalXS,
  },
  gapMd: {
    gap: tokens.spacingHorizontalS,
  },

  // Individual badge pill styling
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    paddingBlock: tokens.spacingVerticalXXS,
    paddingInline: tokens.spacingHorizontalSNudge,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
    maxWidth: '80px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '1.2',
    cursor: 'default',
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFaster,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  badgeInteractive: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      border: `1px solid ${tokens.colorNeutralStroke1Hover}`,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '1px',
    },
  },
  badgeActive: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    color: tokens.colorBrandForeground1,
  },
  badgeWarning: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    color: tokens.colorPaletteYellowForeground2,
  },
  badgeError: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    color: tokens.colorPaletteRedForeground1,
  },

  badgeIcon: {
    fontSize: tokens.fontSizeBase100,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  badgeLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  // Overflow indicator (+N more)
  overflowBadge: {
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

/**
 * Renders a row of compact info badges below a workflow node.
 * Used to show model info, tool counts, connection status, etc.
 */
export const NodeInfoBadges = memo<NodeInfoBadgesProps>(({
  badges,
  visible = true,
  maxBadges = 3,
  gap = 'sm',
  className,
}) => {
  const styles = useStyles();

  if (badges.length === 0) {
    return null;
  }

  // Determine which badges to show
  const visibleBadges = badges.slice(0, maxBadges);
  const overflowCount = badges.length - maxBadges;

  const getGapClass = () => {
    switch (gap) {
      case 'xs':
        return styles.gapXs;
      case 'md':
        return styles.gapMd;
      default:
        return styles.gapSm;
    }
  };

  const getVariantClass = (variant: BadgeItem['variant']) => {
    switch (variant) {
      case 'active':
        return styles.badgeActive;
      case 'warning':
        return styles.badgeWarning;
      case 'error':
        return styles.badgeError;
      default:
        return undefined;
    }
  };

  const renderBadge = (badge: BadgeItem) => {
    const badgeClasses = mergeClasses(
      styles.badge,
      getVariantClass(badge.variant),
      badge.onClick && styles.badgeInteractive
    );

    const content = (
      <span
        key={badge.id}
        className={badgeClasses}
        role={badge.onClick ? 'button' : undefined}
        tabIndex={badge.onClick ? 0 : undefined}
        onClick={badge.onClick}
        onKeyDown={badge.onClick ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            badge.onClick?.();
          }
        } : undefined}
        aria-label={badge.tooltip ?? badge.label}
      >
        {badge.icon && <span className={styles.badgeIcon}>{badge.icon}</span>}
        <span className={styles.badgeLabel}>{badge.label}</span>
      </span>
    );

    // Wrap in tooltip if tooltip text differs from label
    if (badge.tooltip && badge.tooltip !== badge.label) {
      return (
        <Tooltip
          key={badge.id}
          content={badge.tooltip}
          relationship="description"
          positioning="below"
        >
          {content}
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <div
      className={mergeClasses(
        styles.container,
        getGapClass(),
        visible ? styles.containerVisible : styles.containerHidden,
        className
      )}
      aria-hidden={!visible}
    >
      {visibleBadges.map(renderBadge)}
      {overflowCount > 0 && (
        <Tooltip
          content={`${overflowCount} more: ${badges.slice(maxBadges).map(b => b.label).join(', ')}`}
          relationship="description"
        >
          <span className={mergeClasses(styles.badge, styles.overflowBadge)}>
            +{overflowCount}
          </span>
        </Tooltip>
      )}
    </div>
  );
});

NodeInfoBadges.displayName = 'NodeInfoBadges';

export default NodeInfoBadges;
